<template>
    <div class="modal fade show d-block" tabindex="-1" role="dialog" @click.self="$emit('close')">
        <div class="modal-dialog modal-dialog-centered" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">
                        <i class="fas fa-clock me-2"></i>
                        Time Clock
                    </h5>
                    <button type="button" class="btn-close" @click="$emit('close')" aria-label="Close"></button>
                </div>
                
                <div class="modal-body">
                    <!-- Current Status Display -->
                    <div class="status-card mb-4 p-3 rounded" :class="isClockedIn ? 'bg-success-subtle' : 'bg-secondary-subtle'">
                        <div class="d-flex align-items-center justify-content-between">
                            <div>
                                <div class="status-label text-muted small">Current Status</div>
                                <div class="status-value fs-4 fw-bold" :class="isClockedIn ? 'text-success' : 'text-secondary'">
                                    <i class="fas me-2" :class="isClockedIn ? 'fa-circle-check' : 'fa-circle-xmark'"></i>
                                    {{ statusText }}
                                </div>
                            </div>
                            <div v-if="isClockedIn && formattedElapsedTime" class="text-end">
                                <div class="text-muted small">Elapsed Time</div>
                                <div class="fs-5 fw-semibold">{{ formattedElapsedTime }}</div>
                            </div>
                        </div>
                    </div>

                    <!-- Last Action Info -->
                    <div v-if="lastAction" class="last-action-card mb-4 p-3 bg-light rounded">
                        <div class="text-muted small mb-1">Last Action</div>
                        <div class="d-flex align-items-center">
                            <i class="fas me-2" :class="lastAction === 'in' ? 'fa-right-to-bracket text-success' : 'fa-right-from-bracket text-secondary'"></i>
                            <span class="fw-medium">
                                {{ lastAction === 'in' ? 'Clocked In' : 'Clocked Out' }}
                            </span>
                            <span v-if="formattedLastActionTime" class="ms-2 text-muted">
                                at {{ formattedLastActionTime }}
                            </span>
                        </div>
                    </div>

                    <!-- Weekly Hours -->
                    <div class="weekly-hours-card mb-4 p-3 bg-light rounded">
                        <div class="d-flex align-items-center justify-content-between">
                            <div>
                                <div class="text-muted small">This Week</div>
                                <div class="fs-5 fw-semibold">{{ weeklyHours.toFixed(1) }} hours</div>
                            </div>
                            <div class="progress" style="width: 120px; height: 8px;">
                                <div 
                                    class="progress-bar" 
                                    :class="weeklyHours >= 40 ? 'bg-warning' : 'bg-primary'"
                                    :style="{ width: Math.min((weeklyHours / 40) * 100, 100) + '%' }"
                                ></div>
                            </div>
                        </div>
                    </div>

                    <!-- Error Display -->
                    <div v-if="error" class="alert alert-danger mb-4">
                        <i class="fas fa-exclamation-triangle me-2"></i>
                        {{ error }}
                    </div>

                    <!-- Clock Action Button -->
                    <div class="d-grid">
                        <button 
                            type="button" 
                            class="btn btn-lg"
                            :class="isClockedIn ? 'btn-outline-danger' : 'btn-success'"
                            :disabled="isLoading"
                            @click="handleClockAction"
                        >
                            <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
                            <i v-else class="fas me-2" :class="isClockedIn ? 'fa-right-from-bracket' : 'fa-right-to-bracket'"></i>
                            {{ isClockedIn ? 'Clock Out' : 'Clock In' }}
                        </button>
                    </div>

                    <!-- GPS Info (if available) -->
                    <div v-if="gpsStatus" class="mt-3 text-center">
                        <small class="text-muted">
                            <i class="fas fa-location-dot me-1"></i>
                            {{ gpsStatus }}
                        </small>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div class="modal-backdrop fade show"></div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { useTimeClock } from '@/composables/useTimeClock';

const emit = defineEmits(['close', 'clocked']);

const {
    isClockedIn,
    weeklyHours,
    lastAction,
    formattedLastActionTime,
    formattedElapsedTime,
    statusText,
    isLoading,
    error,
    fetchStatus,
    clockIn,
    clockOut
} = useTimeClock();

const gpsStatus = ref<string | null>(null);
const latitude = ref<number | undefined>(undefined);
const longitude = ref<number | undefined>(undefined);

let elapsedInterval: number | null = null;

const getGPSLocation = (): Promise<GeolocationPosition | null> => {
    return new Promise((resolve) => {
        if (!navigator.geolocation) {
            gpsStatus.value = 'GPS not available';
            resolve(null);
            return;
        }

        gpsStatus.value = 'Getting location...';
        
        navigator.geolocation.getCurrentPosition(
            (position) => {
                latitude.value = position.coords.latitude;
                longitude.value = position.coords.longitude;
                gpsStatus.value = `Location acquired (${position.coords.latitude.toFixed(4)}, ${position.coords.longitude.toFixed(4)})`;
                resolve(position);
            },
            (error) => {
                console.warn('GPS error:', error);
                gpsStatus.value = 'Location unavailable';
                resolve(null);
            },
            {
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 60000
            }
        );
    });
};

const handleClockAction = async () => {
    // Try to get GPS location first
    await getGPSLocation();
    
    let result;
    if (isClockedIn.value) {
        result = await clockOut(latitude.value, longitude.value);
    } else {
        result = await clockIn(latitude.value, longitude.value);
    }
    
    if (result) {
        emit('clocked', result);
    }
};

onMounted(async () => {
    // Fetch current status
    await fetchStatus();
    
    // Try to get GPS location in background
    getGPSLocation();
    
    // Update elapsed time every minute
    elapsedInterval = window.setInterval(() => {
        // Force reactivity update for elapsed time
        if (isClockedIn.value) {
            // This will trigger recomputation
        }
    }, 60000);
});

onUnmounted(() => {
    if (elapsedInterval) {
        clearInterval(elapsedInterval);
    }
});
</script>

<style scoped>
.modal.show {
    background-color: rgba(0, 0, 0, 0.5);
}

.status-card {
    border: 1px solid transparent;
}

.bg-success-subtle {
    background-color: rgba(25, 135, 84, 0.1) !important;
    border-color: rgba(25, 135, 84, 0.2) !important;
}

.bg-secondary-subtle {
    background-color: rgba(108, 117, 125, 0.1) !important;
    border-color: rgba(108, 117, 125, 0.2) !important;
}

.progress {
    background-color: #e9ecef;
    border-radius: 4px;
}
</style>
